import React, { useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';
import { mapSupabaseErrorMessage } from './authErrors';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../components/Logo';
import SupportChat from '../../components/SupportChat';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Check if the email exists by attempting to sign in with a dummy password
    // This helps us determine if the user exists before sending a reset email
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: 'dummy_check_password_12345!@#$%^&*()',
    });

    // Check if the error specifically indicates the user doesn't exist
    if (signInError) {
      const errorMessage = signInError.message.toLowerCase();
      
      // If error specifically says user_not_found, user doesn't exist
      if (errorMessage.includes('user_not_found')) {
        toast.error('No account found for this email address. Please sign up first.');
        setIsLoading(false);
        return;
      }
      
      // For "invalid login credentials", Supabase doesn't differentiate between
      // wrong password and non-existent user for security reasons.
      // We'll proceed to resetPasswordForEmail and check its error response.
    }

    // Proceed with password reset
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: undefined, // No redirect, we'll use OTP code
    });

    if (resetError) {
      const errorMsg = resetError.message.toLowerCase();
      // Check if the error indicates user doesn't exist
      if (errorMsg.includes('user_not_found') || 
          errorMsg.includes('email not found') ||
          errorMsg.includes('user does not exist')) {
        toast.error('No account found for this email address. Please sign up first.');
      } else {
        // For other errors, show toast
        toast.error(mapSupabaseErrorMessage(resetError.message));
      }
      setIsLoading(false);
      return;
    }
    
    // Success - email sent (only if user exists)
    toast.success('Check your email for codes');
    navigate('/reset-password/verify', { state: { email: email.trim() } });
    setIsLoading(false);
  };

  return (
    <div className="relative min-h-screen w-full flex items-start justify-center bg-[#0d1830] pt-20">
      <AnimatedBackground />
      <style>{`
        /* Override autofill styles for all states */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: white !important;
          transition: background-color 5000s ease-in-out 0s !important;
          box-shadow: inset 0 0 0 1000px rgba(255, 255, 255, 0.08) !important;
          caret-color: white !important;
        }
        
        /* Ensure the fields maintain styling when typing after autofill */
        .forgot-password-input {
          background: rgba(255, 255, 255, 0.08) !important;
          backdrop-filter: blur(5px) !important;
          -webkit-backdrop-filter: blur(5px) !important;
          color: white !important;
          -webkit-text-fill-color: white !important;
        }
      `}</style>
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        <div className="mb-4 pb-8 w-full">
          <Logo />
        </div>
        <div
          className="relative p-8 rounded-2xl w-full max-w-md"
          style={{
            background: 'rgba(0, 0, 0, 0.01)',
            backdropFilter: 'blur(3px) saturate(120%)',
            WebkitBackdropFilter: 'blur(3px) saturate(120%)',
            border: '0.5px solid rgba(255, 255, 255, 0.04)',
            boxShadow: '0 15px 32px 0 rgba(255, 255, 255, 0.2)'
          }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-light text-white/90 tracking-wide">Forgot Password</h2>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="forgot-password-input w-full px-3 py-2.5 rounded-lg text-white placeholder-white/90 border border-white/30 transition-all duration-300 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 rounded-lg font-semibold tracking-wide transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'rgba(23, 196, 128, 0.9)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: '#0d1830'
              }}
            >
              {isLoading ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
          <div className="mt-3 text-white text-sm text-center">
            <span>
              Remember password?{' '}
              <Link to="/login" className="inline-block py-1 underline text-white hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] focus:ring-offset-2 focus:ring-offset-[#0d1830] rounded">
                Login
              </Link>
            </span>
          </div>
          <div className="mt-4 text-white text-xs text-center">
            <p>If you have an Engramiq account you will receive an email with password reset instructions.</p>
          </div>
          <div className="mt-4 text-white/70 text-xs text-center">
            <p>Need help? Contact <a href="mailto:support@engramiq.com" className="underline hover:text-[#17c480] transition-colors">support@engramiq.com</a></p>
          </div>
        </div>
      </div>
      {/* <SupportChat /> */}
    </div>
  );
};

export default ForgotPasswordPage;

